import { CURRENCIES, Money, type Currency } from '@chukua/common';
import { Alert, Button, DatePicker, Drawer, Form, Select } from 'antd';
import type { Dayjs } from 'dayjs';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { useSubmission } from '../../../../../shared/hooks/use-submission';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { AmountFields } from '../../../components/AmountFields';
import { FreeQuantityField } from './FreeQuantityField';
import { createAccessorialRate, type AccessorialType } from '../../../rate-cards.api';

interface AddFormValues {
  accessorialTypeCode: string;
  amount: number;
  currency: Currency;
  freeQuantity?: number;
  validFrom: Dayjs;
  validTo?: Dayjs | null;
}

interface AddAccessorialRateDrawerProps {
  open: boolean;
  rateCardId: string;
  types: AccessorialType[];
  onAdded: () => void;
  onClose: () => void;
}

/** Takes effect at once: unlike a rate line, an accessorial rate has no approval step. */
export function AddAccessorialRateDrawer({ open, rateCardId, types, onAdded, onClose }: AddAccessorialRateDrawerProps) {
  const [form] = Form.useForm<AddFormValues>();
  const typeCode = Form.useWatch('accessorialTypeCode', form);
  const { error, submitting, submit, clearError } = useSubmission();
  const toast = useToast();

  async function add(values: AddFormValues) {
    const added = await submit(
      () =>
        createAccessorialRate(rateCardId, {
          accessorialTypeCode: values.accessorialTypeCode,
          amount: Money.parse(String(values.amount), values.currency).toJSON(),
          freeQuantity: values.freeQuantity ?? null,
          validFrom: values.validFrom.format('YYYY-MM-DD'),
          validTo: values.validTo?.format('YYYY-MM-DD'),
        }).then(() => true),
      (err) => apiErrorMessage(err) ?? 'Could not add the rate — check the fields and try again.',
    );
    if (!added) return;
    toast.success('Accessorial rate added');
    form.resetFields();
    onAdded();
    onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} afterOpenChange={clearError} title="Add accessorial rate" placement="right" size={480} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<AddFormValues> form={form} layout="vertical" requiredMark={false} onFinish={add} disabled={submitting} initialValues={{ currency: CURRENCIES[0] }}>
        <Form.Item name="accessorialTypeCode" label="Charge type" rules={[{ required: true, message: 'Choose a charge type' }]}>
          <Select options={types.filter((type) => type.isActive).map((type) => ({ value: type.code, label: type.name }))} />
        </Form.Item>
        <AmountFields />
        <FreeQuantityField type={types.find((type) => type.code === typeCode)} />
        <div className="grid grid-cols-2 gap-3">
          <Form.Item name="validFrom" label="Effective from" rules={[{ required: true, message: 'Choose a date' }]}>
            <DatePicker className="w-full" />
          </Form.Item>
          <Form.Item name="validTo" label="Until (optional)">
            <DatePicker className="w-full" />
          </Form.Item>
        </div>
        <Button type="primary" htmlType="submit" loading={submitting}>
          Add rate
        </Button>
      </Form>
    </Drawer>
  );
}
